/**
 * useHydrateSurvey — fills SurveyContext from GET /UserProfile/BuddyPreferences
 * so the portrait and the edit-survey flow start from the member's saved
 * answers instead of whatever this device happens to have cached.
 */
import { useEffect, useRef, useState } from 'react';
import userProfileApi from '../api/userProfile/userProfileApi';
import { mapPreferencesToSurveyState } from '../api/onboarding/onboardingMapper';
import { useSurvey } from '../context/SurveyContext';
import { hasExistingQuestionnaireData } from '../utils/questionnaire';

export const useHydrateSurvey = (enabled = true): { loading: boolean } => {
  const { surveyData, hydrateSurvey } = useSurvey();
  const [loading, setLoading] = useState(false);
  /**
   * The one request this hook instance makes, kept across effect teardowns.
   * React Strict Mode runs an effect, cleans it up and runs it again, so a
   * "fetched already" flag alone would leave the second run with nothing to
   * wait on and the caller stuck on its loading state forever. Every run
   * instead waits on this promise, and only a settled request stops the wait.
   */
  const request = useRef<Promise<void> | null>(null);
  const settled = useRef(false);
  // Read through a ref so a context update does not re-trigger the fetch.
  const surveyRef = useRef(surveyData);
  surveyRef.current = surveyData;
  const hydrateRef = useRef(hydrateSurvey);
  hydrateRef.current = hydrateSurvey;

  useEffect(() => {
    if (!enabled || settled.current) return undefined;

    if (!request.current) {
      // Answers already in context are either freshly edited or the last save,
      // so a fetch here would only overwrite them mid-flow.
      if (hasExistingQuestionnaireData(surveyRef.current)) {
        settled.current = true;
        return undefined;
      }

      request.current = userProfileApi
        .getUserBuddyPreferences()
        .then(response => {
          const body = response?.data;
          if (body?.success) {
            hydrateRef.current(mapPreferencesToSurveyState(body));
          }
        })
        .catch(() => {
          // 404 means the member never answered; the empty context is already right.
        })
        .finally(() => {
          settled.current = true;
        });
    }

    let active = true;
    setLoading(true);
    request.current.finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [enabled]);

  return { loading };
};

export default useHydrateSurvey;
